// api/index.ts — Agrégateur du client API (S6 du plan TS+Vite, mai 2026).
//
// Compose les modules par ressource (auth.ts, users.ts, projects.ts, …) en un
// seul objet `api` 100 % rétrocompatible avec l'ancien `api.ts` monolithique.
// Les call-sites continuent d'utiliser `window.api.X()` ou `api.X()` en bare
// ref ; pas besoin de modifier les ~30 modules consommateurs.
//
// Le shim `window.api = api` est posé dans le re-export `app/static/js/api.ts`
// qui est encore le point d'entrée chargé par main.ts pour l'instant.

import {
  downloadBlob,
  downloadBlobWithFilename,
  triggerBlobDownload,
  upload,
  withButton,
} from './_internals.js';
import { accessMatrixApi } from './access-matrix.js';
import { actionPlansApi } from './action-plans.js';
import { actionsApi } from './actions.js';
import { assistantApi } from './assistant.js';
import { auditLogApi } from './audit-log.js';
import { backlogReviewsApi } from './backlog-reviews.js';
import { authApi } from './auth.js';
import { bugsApi } from './bugs.js';
import { checklistsApi } from './checklists.js';
import { committeesApi } from './committees.js';
import { genericRolesApi } from './generic-roles.js';
import { i18nApi } from './i18n.js';
import { iaConsentApi } from './ia-consent.js';
import { iaCreditsApi } from './ia-credits.js';
import { invitationPointsApi } from './invitation-points.js';
import { mascotApi } from './mascot.js';
import { meetingApi } from './meeting.js';
import { meetingGuidesApi } from './meeting-guides.js';
import { meetingsRecordsApi } from './meetings-records.js';
import { metaApi } from './meta.js';
import { notificationsApi } from './notifications.js';
import { postitsApi } from './postits.js';
import { programsApi } from './programs.js';
import { projectsApi } from './projects.js';
import { pushApi } from './push.js';
import { revisionsApi } from './revisions.js';
import { riskColorsApi } from './risk-colors.js';
import { riskListsApi } from './risk-lists.js';
import { stagesApi } from './stages.js';
import { statsApi } from './stats.js';
import { structuresApi } from './structures.js';
import { surveysApi } from './surveys.js';
import { tenantSettingsApi } from './tenant-settings.js';
import { usersApi } from './users.js';
import { workspaceApi } from './workspace.js';

export const api = {
  // Helpers transverses (utilisés en bare ref par les modules pages).
  withButton,
  downloadBlob,
  downloadBlobWithFilename,
  triggerBlobDownload,
  upload,

  // Ressources (à plat, comme l'ancien api.ts pour rétrocompat).
  ...accessMatrixApi,
  ...actionPlansApi,
  ...actionsApi,
  ...assistantApi,
  ...auditLogApi,
  ...authApi,
  ...backlogReviewsApi,
  ...bugsApi,
  ...checklistsApi,
  ...committeesApi,
  ...genericRolesApi,
  ...i18nApi,
  ...iaConsentApi,
  ...iaCreditsApi,
  ...invitationPointsApi,
  ...mascotApi,
  ...meetingApi,
  ...meetingGuidesApi,
  ...meetingsRecordsApi,
  ...metaApi,
  ...notificationsApi,
  ...postitsApi,
  ...programsApi,
  ...projectsApi,
  ...pushApi,
  ...revisionsApi,
  ...riskColorsApi,
  ...riskListsApi,
  ...stagesApi,
  ...statsApi,
  ...structuresApi,
  ...surveysApi,
  ...tenantSettingsApi,
  ...usersApi,
  ...workspaceApi,
};

// Le type ApiClient correspond exactement à la forme runtime de `api`.
export type ApiClient = typeof api;

declare global {
  interface Window {
    api: ApiClient;
  }
}

export type { ActionMutationOptions, HttpMethod } from './_internals.js';
